import { CareerLevelRail } from './CareerJourney';
import { useCareer } from './Gamification';
import DesktopDailyGoals from "@/components/focus/DesktopDailyGoals";
import MobileMonthlyGoal from "@/components/focus/MobileMonthlyGoal";
import AreaNavLink from "@/components/AreaNavLink";
import { appStorage } from "@/lib/employeePreview";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { warmCallModes } from "@/lib/callPrefetch";
import { Briefcase, Trophy, Phone, Users, Megaphone, ChevronDown } from "lucide-react";
import "./MobileSalesConsole.css";
import "./PrecisionCockpit.css";
import "./ConsoleRetraction.css";
import armatureMobile from "@/assets/armature-mobile-v2.webp";
import DesktopArmature from './DesktopArmature';
import "./ResponsiveArmature.css";

const ACTIONS = [
  { to: "/m/call", label: "Cold Call", icon: Phone, area: "cold_call" },
  { to: "/m/my-contacts", label: "Meine Kontakte", icon: Users, area: "my_contacts" },
  { to: "/m/promo", label: "Promo", icon: Megaphone, area: "promo" },
];

const COLLAPSED_KEY = "ls_console_collapsed";
function savedCollapsedState() {
  try { return appStorage.getItem(COLLAPSED_KEY) === "1"; }
  catch (_) { return false; }
}

function ConsoleBriefcase() {
  return <svg viewBox="0 0 48 44" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="14" width="38" height="26" rx="2"/><path d="M17 14V8h14v6"/><path d="M22 24h4v4h-4z" fill="currentColor" stroke="none"/></svg>;
}

function MoneyBag() {
  return <svg viewBox="0 0 64 64" aria-hidden="true" className="sales-console-bag">
    <path fill="currentColor" d="M23 17 18 7c4-3 8 1 11 0 3-5 6-5 9 0 3 1 7-3 10 0l-6 10H23Zm-1 8c-5 5-13 15-13 24 0 11 10 13 23 13s23-2 23-13c0-9-8-19-13-24H22Z" />
    <path d="M24 21h17" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
    <path d="M39 35c-8-6-15 0-15 9s7 15 15 9M20 41h15M20 47h13" fill="none" stroke="#e6c16a" strokeWidth="3" strokeLinecap="round" />
  </svg>;
}

export default function MobileSalesConsole() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [mobile,setMobile]=useState(()=>window.matchMedia("(max-width: 480px)").matches);
  const [careerNarrow, setCareerNarrow] = useState(() => window.matchMedia("(max-width: 1100px)").matches);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(savedCollapsedState);
  const collapseTrigger = useRef(null);
  const carriage = useRef(null);
  const trigger = useRef(null);
  const firstAction = useRef(null);
  const panelId = useId();
  const dockId = useId();
  const actions = ACTIONS;
  const ornate = true;
  const { career } = useCareer();
  const careerMobile = mobile && pathname === "/m/career";
  const showCareerRail = careerNarrow && pathname === "/m/career";
  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  };
  const toggleMoneyBag = () => {
    const next = !open;
    setOpen(next);
    if (next) warmCallModes(user, { full: true, userInitiated: true }).catch(() => {});
  };
  const toggleConsole = () => {
    // Keep focus on the visible handle before making the instruments inert.
    collapseTrigger.current?.focus({ preventScroll: true });
    setOpen(false);
    const next = !collapsed;
    setCollapsed(next);
    try { appStorage.setItem(COLLAPSED_KEY, next ? "1" : "0"); }
    catch (_) { /* The handle still works when browser storage is unavailable. */ }
  };
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 481px)");
    const resize = () => { setMobile(!media.matches); if (media.matches) setOpen(false); };
    media.addEventListener("change", resize);
    return () => media.removeEventListener("change", resize);
  }, []);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 1100px)");
    const resize = () => setCareerNarrow(media.matches);
    media.addEventListener("change", resize);
    return () => media.removeEventListener("change", resize);
  }, []);
  useEffect(() => {
    if (!open) return;
    firstAction.current?.focus({ preventScroll: true });
    const escape = (event) => {
      if (event.key === "Escape") { setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [open]);

  useLayoutEffect(() => {
    const housing = carriage.current;
    const button = trigger.current;
    if (mobile || !housing || !button) return;
    // The instrument grid is asymmetric; follow the moneybag, not the viewport.
    const align = () => {
      const face = button.getBoundingClientRect();
      const frame = housing.getBoundingClientRect();
      housing.style.setProperty("--console-toggle-x", `${face.left + face.width / 2 - frame.left}px`);
    };
    align();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(align);
    observer?.observe(housing);
    observer?.observe(button);
    window.addEventListener("resize", align);
    return () => { observer?.disconnect(); window.removeEventListener("resize", align); };
  }, [mobile, showCareerRail, pathname]);

  if (!mobile && !pathname.startsWith("/m/")) return null;
  return <div className={`sales-console${ornate ? " precision-console" : ""}${careerMobile ? " career-console" : ""}`} data-layout={mobile ? "mobile" : "wide"} data-career-rail={showCareerRail} data-open={open} data-collapsed={collapsed} data-testid="sales-console">
    {open && <button type="button" tabIndex={-1} aria-label="Vertriebsaktionen schließen" className="sales-console-backdrop" onPointerDown={event => event.preventDefault()} onClick={() => close(true)} />}
    <div ref={carriage} className="sales-console-carriage">
      {!mobile && <DesktopArmature careerRail={showCareerRail} />}
      <button ref={collapseTrigger} type="button" className="sales-console-collapse" data-testid="console-collapse-toggle"
        aria-label={collapsed ? "Armatur ausklappen" : "Armatur einfahren"}
        title={collapsed ? "Armatur ausklappen" : "Armatur einfahren"}
        aria-expanded={!collapsed} aria-controls={dockId} onClick={toggleConsole}>
        <span className="console-toggle-bezel"><ChevronDown aria-hidden="true" /></span>
      </button>
    <div id={dockId} className="sales-console-dock" inert={collapsed} aria-hidden={collapsed} onBlur={event => {
      if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }}>
      {ornate && mobile && <img src={armatureMobile} alt="" className="precision-plate precision-plate-mobile" draggable="false"/>}
      <div id={panelId} className="sales-console-actions" aria-label="Vertriebsaktionen" aria-hidden={!open} style={{ "--action-count": actions.length || 1 }}>
        {actions.map((action, index) => <AreaNavLink ref={index === 0 ? firstAction : undefined} key={action.to} to={action.to} tabIndex={open ? 0 : -1} onClick={() => close()} style={{ "--action-index": index }} className={({ isActive }) => `sales-console-action${isActive ? " is-active" : ""}`}>
          <span className="sales-console-action-face"><action.icon aria-hidden="true" /><span>{action.label}</span></span>
        </AreaNavLink>)}
      </div>
      <nav className="sales-console-bar" aria-label={mobile ? "Mobile Hauptnavigation" : "Hauptnavigation"}>
        <AreaNavLink to="/m/opportunities" onClick={() => close()} className={({ isActive }) => `sales-console-side${isActive ? " is-active" : ""}`}>{ornate ? <ConsoleBriefcase/> : <Briefcase aria-hidden="true" />}<span>Chancen</span></AreaNavLink>
        <div className="sales-console-socket"><button ref={trigger} type="button" aria-label={open ? "Vertriebsaktionen schließen" : "Vertriebsaktionen öffnen"} aria-expanded={open} aria-controls={panelId} disabled={!actions.length} onClick={toggleMoneyBag} className="sales-console-trigger" data-testid="money-bag-toggle"><MoneyBag /></button></div>
        <AreaNavLink to="/m/career" onClick={() => close()} className={({ isActive }) => `sales-console-side${isActive ? " is-active" : ""}`}><Trophy aria-hidden="true" /><span>Meine Karriere</span></AreaNavLink>
      </nav>
      {showCareerRail ? <CareerLevelRail career={career}/> : mobile ? <MobileMonthlyGoal ornate={ornate} /> : null}
      {!mobile && !showCareerRail && <DesktopDailyGoals />}
    </div>
    </div>
  </div>;
}
